import React from 'react';
import {
  Typography,
  Box,
  Avatar,
  Link as MuiLink,
} from '@mui/material';
import {
  Comment as CommentIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

export default function CommentNotificationCard({ notification, onMarkRead }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const payload = notification.payload || {};
  
  const authorProfileUrl = payload.author_id ? `/profile/${payload.author_id}` : null;
  
  const handleMarkRead = () => {
    if (!notification.is_read && onMarkRead) {
      onMarkRead();
    }
  };
  
  const handleAuthorClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleMarkRead();
    if (authorProfileUrl) {
      navigate(authorProfileUrl);
    }
  };
  
  const handleCardClick = () => {
    handleMarkRead();
    
    // Navigate to the reflection/comment
    if (payload.target_type === 'reflection' && payload.target_id) {
      // Check if we have full navigation data
      if (payload.reflection_author_id && payload.reflection_content_type && payload.reflection_object_id) {
        // Navigate to the specific item reflection page
        navigate(`/profile/${payload.reflection_author_id}/reflections/${payload.reflection_content_type}/${payload.reflection_object_id}`);
      } else {
        // Fallback: just show a message that we can't navigate
        console.warn('Missing navigation data for reflection comment notification');
      }
    }

    
    else if (payload.target_type === 'post' && payload.target_id) {
      // console.log('*** Notification payload:', payload);
      navigate(`/feed/${payload.author_id}#post-${payload.target_id}`);
    } 

    else if (payload.target_type === 'call' && payload.target_id) {
      navigate(`/calls/${payload.target_id}/#comment-${payload.target_id}`);
    }

    else {
      console.warn('Unknown target type for comment notification:', payload.target_type);
    }


  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        transition: 'background-color 0.2s',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      {/* Avatar */}
      <Avatar
        src={payload.author_avatar_url || payload.author_avatar}
        alt={payload.author_name || 'Commenter'}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: theme.palette.primary.avatar,
          color: 'white',
          cursor: authorProfileUrl ? 'pointer' : 'default',
        }}
        onClick={authorProfileUrl ? handleAuthorClick : undefined}
      >
        {payload.author_name?.charAt(0) || <CommentIcon fontSize="small" />}
      </Avatar>

      {/* Single line text */}
      <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {authorProfileUrl ? (
          <MuiLink
            component="a"
            href={authorProfileUrl}
            underline="hover"
            color="text.primary"
            fontWeight={600}
            onClick={handleAuthorClick}
            sx={{ cursor: 'pointer' }}
          >
            {payload.author_name || 'Someone'}
          </MuiLink>
        ) : (
          <Box component="span" fontWeight={600}>
            {payload.author_name || 'Someone'}
          </Box>
        )}
        <CommentIcon sx={{ fontSize: 14, color: 'primary.main', mx: 0.25 }} />
        <Box component="span" color="text.secondary">
          commented
        </Box>
        {payload.target_type && (
          <>
            <Box component="span" color="text.secondary">
              on your {payload.target_type}
            </Box>
          </>
        )}
        {payload.body_preview && (
          <>
            <Box component="span" color="text.secondary">:</Box>
            <Box component="span" color="text.secondary" fontStyle="italic" sx={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              maxWidth: '200px'
            }}>
              "{payload.body_preview}"
            </Box>
          </>
        )}
      </Typography>

      {/* Timestamp and unread indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && (
          <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
        )}
      </Box>
    </Box>
  );
}
